import { cn } from '@/lib/utils';
import {
  Settings,
  CreditCard,
  Users,
  Shield,
  Bell,
  Puzzle,
  Plug,
  Truck,
  Link as LinkIcon,
  Zap,
  Mail,
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    id: 'account',
    label: 'Account',
    items: [
      {
        id: 'general',
        label: 'General',
        href: '/settings/general',
        icon: Settings,
      },
      {
        id: 'users',
        label: 'Users',
        href: '/settings/users',
        icon: Shield,
      },
      {
        id: 'notifications',
        label: 'Notifications',
        href: '/settings/notifications',
        icon: Bell,
      },
      {
        id: 'features',
        label: 'Features',
        href: '/settings/features',
        icon: Puzzle,
      },
    ],
  },
  {
    id: 'store',
    label: 'Store',
    items: [
      {
        id: 'customers',
        label: 'Customers',
        href: '/settings/customers',
        icon: Users,
      },
      {
        id: 'payment-shipping',
        label: 'Payment & Shipping',
        href: '/settings/payment-shipping',
        icon: CreditCard,
      },
    ],
  },
  {
    id: 'connections',
    label: 'Connections',
    items: [
      {
        id: 'channels',
        label: 'Channels',
        href: '/settings/channels',
        icon: LinkIcon,
      },
      {
        id: 'integrations',
        label: 'Integrations',
        href: '/settings/integrations',
        icon: Plug,
      },
      {
        id: 'delivery-providers',
        label: 'Shipping Providers',
        href: '/settings/delivery-providers',
        icon: Truck,
      },
    ],
  },
  {
    id: 'automation',
    label: 'Automation',
    items: [
      {
        id: 'workflows',
        label: 'Workflows',
        href: '/settings/workflows',
        icon: Zap,
      },
      {
        id: 'email-templates',
        label: 'Email Templates',
        href: '/settings/email-templates',
        icon: Mail,
      },
    ],
  },
];

function NavLink({ item, isActive }: { item: NavItem; isActive: boolean }) {
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      className={cn(
        'flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition-colors',
        isActive
          ? 'bg-secondary text-foreground font-medium'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span>{item.label}</span>
    </Link>
  );
}

interface SettingsSidebarProps {
  className?: string;
}

export function SettingsSidebar({ className }: SettingsSidebarProps) {
  const pathname = useLocation().pathname;

  const isActive = (href: string) => {
    const normalizedPath = pathname.replace('/admin', '');
    const normalizedHref = href.replace('/admin', '');
    return normalizedPath === normalizedHref;
  };

  return (
    <div className={cn('w-52 border-r bg-background', className)}>
      <div className="px-3 py-4 space-y-4">
        {navGroups.map((group) => (
          <div key={group.id} className="space-y-0.5">
            <p className="px-2.5 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </p>
            {group.items.map((item) => (
              <NavLink key={item.id} item={item} isActive={isActive(item.href)} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
